import { FC, useState } from 'react';
import { toast } from 'react-toastify';

import { ShareIcon } from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { Company, SharedCapTableHooks } from '../../../hooks/index';
import { SharedCapTableLink } from '../../../hooks/shared-cap-table/shared-cap-table.types';
import { useLocale } from '../../../hooks/use-locale.hook';
import { canShareCapTable } from './share-access.util';
import { ShareCapTableModal, ShareLinkFormValues } from './share-cap-table-modal';
import { ShareConfirmModal, ShareConfirmVariant } from './share-confirm-modal';
import { buildSharedCapTableLinkPatch } from './share-link-patch.util';
import { buildSharedCapTableUrl } from './share-link-url.util';
import { ShareLinksManageModal } from './share-links-manage-modal';

/** The screen underneath. A confirmation never replaces it — see `ShareConfirmStep`. */
enum ShareModalView {
  CLOSED = 'closed',
  EDIT = 'edit',
  FORM = 'form',
  MANAGE = 'manage',
}

/**
 * Confirmations layer over the screen that raised them: deleting a link or saving an edit is
 * answered in the context of the list or the form, not on a bare backdrop.
 */
enum ShareConfirmStep {
  CANCEL_EDIT = 'cancel-edit',
  DELETE = 'delete',
  SAVE_EDIT = 'save-edit',
}

export const ShareCapTableButton: FC = () => {
  const { selectedCompany } = Company.useSelected();
  const { messagesLocale } = useLocale();
  const companyName = selectedCompany?.name ?? '';

  const canManageLinks = canShareCapTable(
    selectedCompany?.roleInCompany,
    selectedCompany?.stakeholder?.accessRole,
  );

  const [view, setView] = useState(ShareModalView.CLOSED);
  const [confirmStep, setConfirmStep] = useState<ShareConfirmStep | undefined>();
  const [editedLink, setEditedLink] = useState<SharedCapTableLink | undefined>();
  // Held while a confirmation is open: the edit form is unmounted, so its values live here.
  const [pendingDelete, setPendingDelete] = useState<SharedCapTableLink | undefined>();
  const [pendingEdit, setPendingEdit] = useState<ShareLinkFormValues | undefined>();

  const { links } = SharedCapTableHooks.useSharedCapTableLinks(canManageLinks);
  const { createLink, isPending: isCreating } = SharedCapTableHooks.useSharedCapTableLinkCreate();
  const { isPending: isUpdating, updateLink } = SharedCapTableHooks.useSharedCapTableLinkUpdate();
  const { deleteLink, isPending: isDeleting } = SharedCapTableHooks.useSharedCapTableLinkDelete();

  const closeModal = () => {
    setView(ShareModalView.CLOSED);
    setConfirmStep(undefined);
    setEditedLink(undefined);
    setPendingDelete(undefined);
    // Carries a plaintext password while a confirmation is open — drop it as soon as it is moot.
    setPendingEdit(undefined);
  };

  // Editing is only reachable from the list, so discarding it returns there rather than closing
  // the modal outright — the owner is mid-way through managing links, not done with them.
  /**
   * Where editing ends, whether it was saved or dropped: the owner came from the list and is not
   * finished with it, so closing the modal outright makes them reopen it to see the result.
   */
  const returnToLinkList = () => {
    setConfirmStep(undefined);
    setEditedLink(undefined);
    setPendingEdit(undefined);
    setView(ShareModalView.MANAGE);
  };

  const handleOpen = () => setView(links.length > 0 ? ShareModalView.MANAGE : ShareModalView.FORM);

  const handleCreate = ({ expireInterval, note, password, settings }: ShareLinkFormValues) =>
    createLink(
      {
        expireInterval: expireInterval ?? null,
        note: note || null,
        password: password || null,
        settings,
      },
      {
        // The link already exists at this point — a clipboard failure (insecure context, denied
        // permission, unfocused tab) must not leave the owner on the form, or they retry and burn
        // slots from the active-link limit. Either way they land on the list, where the new link is
        // waiting with a Copy of its own.
        onSuccess: async (link) => {
          const url = buildSharedCapTableUrl(companyName, link.linkId);

          try {
            await navigator.clipboard.writeText(url);
            toast(messagesLocale['SHARE_LINK_CREATED_AND_COPIED']);
          } catch {
            toast(messagesLocale['SHARE_LINK_COPY_FAILED']);
          } finally {
            returnToLinkList();
          }
        },
      },
    );

  // Editing is confirmed before it lands, because saved changes reach every viewer immediately.
  const handleUpdate = () => {
    if (!editedLink || !pendingEdit) {
      return;
    }

    updateLink(
      { ...buildSharedCapTableLinkPatch(pendingEdit), id: editedLink.id },
      {
        onSuccess: returnToLinkList,
      },
    );
  };

  const handleDelete = () => {
    if (!pendingDelete) {
      return;
    }

    deleteLink(
      { id: pendingDelete.id },
      {
        onSuccess: () => {
          setConfirmStep(undefined);
          setPendingDelete(undefined);
        },
      },
    );
  };

  if (!canManageLinks) {
    return null;
  }

  return (
    <>
      <AlertDialogWrapper
        control={{ open: view !== ShareModalView.CLOSED, onOpenChange: closeModal }}
      >
        {view === ShareModalView.MANAGE && (
          <ShareLinksManageModal
            companyName={companyName}
            isPending={isDeleting}
            links={links}
            onCancel={closeModal}
            onCreate={() => setView(ShareModalView.FORM)}
            onDelete={(link) => {
              setPendingDelete(link);
              setConfirmStep(ShareConfirmStep.DELETE);
            }}
            onEdit={(link) => {
              setEditedLink(link);
              setView(ShareModalView.EDIT);
            }}
          />
        )}

        {(view === ShareModalView.FORM || view === ShareModalView.EDIT) && (
          <ShareCapTableModal
            editedLink={view === ShareModalView.EDIT ? editedLink : undefined}
            isPending={isCreating || isUpdating}
            onCancel={
              view === ShareModalView.EDIT
                ? () => setConfirmStep(ShareConfirmStep.CANCEL_EDIT)
                : closeModal
            }
            onSubmit={
              view === ShareModalView.EDIT
                ? (values) => {
                    setPendingEdit(values);
                    setConfirmStep(ShareConfirmStep.SAVE_EDIT);
                  }
                : handleCreate
            }
          />
        )}

        {/* Its own full-screen layer above the screen underneath, so answering a confirmation
            never takes the list or the form off screen. */}
        {confirmStep && (
          <div className="fixed inset-0 z-[62] flex items-center justify-center bg-gray-900/40 p-2">
            {confirmStep === ShareConfirmStep.DELETE && (
              <ShareConfirmModal
                isPending={isDeleting}
                onCancel={() => setConfirmStep(undefined)}
                onConfirm={handleDelete}
                variant={ShareConfirmVariant.DELETE}
              />
            )}

            {confirmStep === ShareConfirmStep.SAVE_EDIT && (
              <ShareConfirmModal
                isPending={isUpdating}
                onCancel={() => setConfirmStep(undefined)}
                onConfirm={handleUpdate}
                variant={ShareConfirmVariant.SAVE_EDIT}
              />
            )}

            {confirmStep === ShareConfirmStep.CANCEL_EDIT && (
              <ShareConfirmModal
                onCancel={() => setConfirmStep(undefined)}
                onConfirm={returnToLinkList}
                variant={ShareConfirmVariant.CANCEL_EDIT}
              />
            )}
          </div>
        )}
      </AlertDialogWrapper>

      <Button
        aria-label={messagesLocale['SHARE']}
        className="flex size-9 items-center justify-center gap-1 border-[1px] border-gray-100 bg-gray-25 py-[6px] lg:w-fit lg:px-3"
        onClick={handleOpen}
      >
        <ShareIcon />
        <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
          <AppFormattedMessage id="SHARE" />
        </span>
      </Button>
    </>
  );
};
